import React, { useEffect, useMemo, useRef, useState } from "react";
import { Country, City } from "country-state-city";
import { ChevronDown, MapPin, X } from "lucide-react";

// IP-based country detect with graceful fallback to India (IN)
export const detectCountryCode = async () => {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    const r = await fetch("https://ipapi.co/json/", { signal: controller.signal });
    clearTimeout(timeout);
    if (!r.ok) throw new Error("ip fail");
    const data = await r.json();
    if (data && data.country_code && Country.getCountryByCode(data.country_code)) {
      return data.country_code;
    }
  } catch { /* fall through */ }
  return "IN";
};

const allCountries = Country.getAllCountries().map((c) => ({
  code: c.isoCode, name: c.name, flag: c.flag,
}));

const Sheet = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center" data-testid="picker-sheet">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-[430px] bg-white rounded-t-[32px] p-5 pb-8 h-[75dvh] flex flex-col">
        <div className="w-12 h-1.5 rounded-full bg-[#E7E5E4] mx-auto mb-4" />
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-instrument text-xl font-semibold text-[#0A4C36]">{title}</h3>
          <button onClick={onClose} data-testid="picker-close"><X size={20} className="text-[#78716C]" /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

export const CountryPicker = ({ value, onChange, testid = "country", required = false }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = allCountries.find((c) => c.code === value);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return allCountries;
    return allCountries.filter((c) => c.name.toLowerCase().includes(s));
  }, [q]);

  return (
    <>
      <button
        type="button"
        data-testid={`${testid}-picker`}
        onClick={() => { setOpen(true); setQ(""); }}
        className="input-field flex items-center justify-between text-left"
      >
        <span className={selected ? "text-[#1C1917]" : "text-[#A8A29E]"}>
          {selected ? (
            <span className="flex items-center gap-2">
              <span className="text-lg">{selected.flag}</span>
              <span>{selected.name}</span>
            </span>
          ) : (
            <>Select country{required ? " *" : ""}</>
          )}
        </span>
        <ChevronDown size={18} className="text-[#78716C]" />
      </button>
      <Sheet open={open} onClose={() => setOpen(false)} title="Select country">
        <input
          autoFocus
          data-testid={`${testid}-search`}
          value={q} onChange={(e) => setQ(e.target.value)}
          className="input-field mb-3" placeholder="Search country…"
        />
        <div className="flex-1 overflow-y-auto -mx-2 px-2">
          {filtered.map((c) => (
            <button
              key={c.code} type="button"
              data-testid={`country-option-${c.code}`}
              onClick={() => { onChange(c.code); setOpen(false); setQ(""); }}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-colors ${
                value === c.code ? "bg-[#FCD9C8] text-[#0A4C36]" : "hover:bg-[#FFF7EE]"
              }`}
            >
              <span className="text-xl">{c.flag}</span>
              <span className="flex-1 text-[#1C1917] text-sm">{c.name}</span>
              {value === c.code && <span className="text-xs text-[#F4844B] font-medium">Selected</span>}
            </button>
          ))}
          {filtered.length === 0 && <div className="text-center text-sm text-[#78716C] py-8">No matches</div>}
        </div>
      </Sheet>
    </>
  );
};

export const CityPicker = ({ countryCode, value, onChange, testid = "city", required = false }) => {
  const [focus, setFocus] = useState(false);
  const [q, setQ] = useState(value || "");
  const [hi, setHi] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  const cities = useMemo(() => {
    if (!countryCode) return [];
    const list = City.getCitiesOfCountry(countryCode) || [];
    // dedupe on name for cleaner suggestions
    const seen = new Set();
    const out = [];
    for (const c of list) {
      const n = c.name.trim();
      const key = n.toLowerCase();
      if (!seen.has(key)) { seen.add(key); out.push(n); }
    }
    return out.sort();
  }, [countryCode]);

  const suggestions = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s || !countryCode) return [];
    const starts = [];
    const contains = [];
    for (const name of cities) {
      const lower = name.toLowerCase();
      if (lower === s) continue;
      if (lower.startsWith(s)) starts.push(name);
      else if (lower.includes(s)) contains.push(name);
      if (starts.length >= 8) break;
    }
    return [...starts, ...contains].slice(0, 8);
  }, [q, cities, countryCode]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setFocus(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (name) => { onChange(name); setQ(name); setFocus(false); };

  const onKey = (e) => {
    if (!focus || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(suggestions[hi]); }
    else if (e.key === "Escape") { setFocus(false); }
  };

  const disabled = !countryCode;

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <MapPin size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A8A29E] pointer-events-none" />
        <input
          data-testid={`${testid}-input`}
          disabled={disabled}
          value={q}
          onFocus={() => { setFocus(true); setHi(0); }}
          onChange={(e) => { setQ(e.target.value); onChange(e.target.value); setFocus(true); setHi(0); }}
          onKeyDown={onKey}
          className={`input-field pl-11 ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          placeholder={disabled ? "Select country first" : `Type a city name${required ? " *" : ""}`}
          autoComplete="off"
        />
      </div>
      {focus && suggestions.length > 0 && (
        <ul
          data-testid={`${testid}-suggestions`}
          className="absolute z-40 mt-1 w-full bg-white rounded-2xl shadow-xl border border-[#0A4C36]/10 py-2 max-h-64 overflow-y-auto"
        >
          {suggestions.map((s, i) => (
            <li key={s}>
              <button
                type="button"
                data-testid={`${testid}-option-${i}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(s)}
                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 ${
                  i === hi ? "bg-[#FCD9C8] text-[#0A4C36]" : "text-[#1C1917] hover:bg-[#FFF7EE]"
                }`}
              >
                <MapPin size={14} className="text-[#F4844B]" />
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
